import { useMemo } from "react";
import { appsConfig } from "../config/appsConfig";
import {
  accessNodeId,
  buildAccessCatalog,
  tenantAllowedNodeIds,
} from "../config/access/catalog";
import { actionNodeId, resolveAccess } from "../config/access/resolve";
import type {
  AccessAction,
  AccessCatalog,
  EffectiveAccess,
} from "../config/access/types";
import { useAccessStore } from "../store/accessStore";
import { isOwnerUser } from "./useIsReseller";

const catalog = buildAccessCatalog(appsConfig);
const tenantAllowed = tenantAllowedNodeIds(appsConfig);

export interface AccessResult {
  catalog: AccessCatalog;
  tenantAllowed: Set<string>;
  effective: EffectiveAccess | null;
  isEnforced: boolean;
  isResolved: boolean;
  isLoading: boolean;
  hasFailed: boolean;
  canAccessNode: (nodeId: string) => boolean;
  canAccessCategory: (categoryId: string) => boolean;
  canAccessApp: (categoryId: string, appId: string) => boolean;
  canAccessSection: (
    categoryId: string,
    appId: string,
    sectionId: string,
  ) => boolean;
  canAccessAppOfId: (appId: string) => boolean;
  canAccessSectionOfApp: (appId: string, sectionId: string) => boolean;
  can: (nodeId: string, action?: AccessAction) => boolean;
  canOfApp: (appId: string, sectionId: string, action?: AccessAction) => boolean;
}

export function useAccess(): AccessResult {
  const status = useAccessStore((state) => state.status);
  const layers = useAccessStore((state) => state.layers);

  return useMemo(() => {
    // El owner (Propietario) nunca queda bloqueado: el API ya lo resuelve a
    // "sin restricciones" (`bypass`), y aquí se repite por si /access/me falla.
    const isOwner = isOwnerUser() || layers?.bypass === true;
    const hasFailed = status === "failed" && !isOwner;
    const isResolved = status === "ready" || status === "failed";
    const isLoading = status === "loading";
    const role = isOwner ? null : (layers?.role ?? null);
    const user = isOwner ? null : (layers?.user ?? null);
    const company = isOwner ? null : (layers?.company ?? null);
    const isEnforced =
      hasFailed || role !== null || user !== null || company !== null;

    const effective = hasFailed
      ? { allowed: new Set<string>(), sourceOf: {} }
      : isEnforced
        ? resolveAccess({ tenantAllowed, company, role, user })
        : null;

    const canAccessNode = (nodeId: string): boolean => {
      if (effective === null) return true;
      return effective.allowed.has(nodeId);
    };

    const can = (nodeId: string, action?: AccessAction): boolean => {
      if (effective === null) return true;
      if (action === undefined) return effective.allowed.has(nodeId);
      return effective.allowed.has(actionNodeId(nodeId, action));
    };

    return {
      catalog,
      tenantAllowed,
      effective,
      isEnforced,
      isResolved,
      isLoading,
      hasFailed,
      canAccessNode,
      canAccessCategory: (categoryId) => canAccessNode(accessNodeId(categoryId)),
      canAccessApp: (categoryId, appId) =>
        canAccessNode(accessNodeId(categoryId, appId)),
      canAccessSection: (categoryId, appId, sectionId) =>
        canAccessNode(accessNodeId(categoryId, appId, sectionId)),
      canAccessAppOfId: (appId) => {
        const categories = catalog.appCategories[appId] ?? [];
        if (categories.length === 0) return true;

        return categories.some((categoryId) =>
          canAccessNode(accessNodeId(categoryId, appId)),
        );
      },
      canAccessSectionOfApp: (appId, sectionId) => {
        const categories = catalog.appCategories[appId] ?? [];
        if (categories.length === 0) return true;

        return categories.some((categoryId) =>
          canAccessNode(accessNodeId(categoryId, appId, sectionId)),
        );
      },
      can,
      canOfApp: (appId, sectionId, action) => {
        const categories = catalog.appCategories[appId] ?? [];
        if (categories.length === 0) return true;

        return categories.some((categoryId) =>
          can(accessNodeId(categoryId, appId, sectionId), action),
        );
      },
    };
  }, [status, layers]);
}

export default useAccess;
